import { extractUkPostcode } from "./ukPostcode";
import {
  syncPickupDatetimeMirrorFields,
  ukDatetimeLocalToIso,
} from "./dateUtils";

/** Partial dashboard booking fields seeded from website audit progress. */
export type AuditBookingPrefillFields = {
  pasengerName?: string;
  surName?: string;
  cellno?: string;
  email?: string;
  vehicleTypeId?: string;
  vehicleTypeName?: string;
  pickupDateTime?: string;
  pickupTime?: string;
  pickupDateWithTime?: string;
  pickupDateTimeStr?: string;
  pickupTimeSpan?: string;
  pickupTimeString?: string;
  manualPickupAddress?: string;
  startpoint?: string;
  pickupPostalCode?: string;
  pickupLatitude?: string;
  pickupLongitude?: string;
  manualDropOffAddress?: string;
  endposition?: string;
  dropoffPostalCode?: string;
  dropoffLatitude?: string;
  dropoffLongitude?: string;
  noOfAdult?: number;
  noOfChild?: number;
  noofPassenger?: number;
  luggagekg?: number;
  luggageDesc?: string;
  childSeat?: number;
  disableSeat?: number;
  returnTrip?: number;
  meetGreet?: number;
  flightNo?: string;
  arrivalFrom?: string;
  terminal?: string;
  additionalInfo?: string;
  paymentMode?: string;
  amount?: number;
  calculatedfare?: number;
  estimateFareNet?: number;
  returnDate?: string;
  returnTime?: string;
  returnTimeStr?: string;
  bookingSource?: string;
};

export type AuditCreateBookingState = {
  auditPrefill: Record<string, unknown>;
  bookingSource?: string;
};

function asTrimmedString(value: unknown): string {
  if (value == null) return "";
  return String(value).trim();
}

function isMasked(key: string, value: unknown): boolean {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = key.toLowerCase();
  if (lower.includes("email")) {
    return trimmed === "***" || /^\S\*\*\*@/.test(trimmed);
  }
  if (
    lower.includes("mobile") ||
    lower.includes("phone") ||
    lower.includes("cell")
  ) {
    const digits = trimmed.replace(/\D/g, "");
    return trimmed.startsWith("***") && digits.length <= 4;
  }
  if (
    lower === "firstname" ||
    lower === "surname" ||
    lower === "lastname" ||
    lower === "passengername"
  ) {
    return /^\S\*\*\*$/.test(trimmed);
  }
  return false;
}

function asFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function asBoolFlag(value: unknown): number | undefined {
  if (value === true || value === 1 || value === "1" || value === "true") return 1;
  if (value === false || value === 0 || value === "0" || value === "false") return 0;
  return undefined;
}

function normalizeTimeHHmm(value: string): string {
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return "";
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function buildPickupDatetimeLocal(
  pickupDate: string,
  pickupTime: string,
): string {
  const date = pickupDate.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const time = normalizeTimeHHmm(pickupTime) || "00:00";
  return `${date}T${time}`;
}

function mapPaymentMode(value: unknown): string | undefined {
  const raw = asTrimmedString(value).toLowerCase();
  if (!raw) return undefined;
  if (raw === "cash" || raw.includes("cash")) return "cash";
  if (raw === "card" || raw.includes("card") || raw === "stripe") return "card";
  if (raw.includes("paypal")) return "paypal";
  return raw;
}

function buildCellno(progress: Record<string, unknown>): string {
  const mobile = asTrimmedString(progress.mobile);
  if (!mobile || isMasked("mobile", mobile)) return "";
  if (mobile.startsWith("+") || mobile.startsWith("00")) return mobile;
  const code = asTrimmedString(progress.mobileCountryCode).replace(/\s/g, "");
  if (!code) return mobile;
  const normalizedCode = code.startsWith("+") ? code : `+${code.replace(/^\+*/, "")}`;
  const digits = mobile.replace(/^0+/, "");
  return `${normalizedCode}${digits}`;
}

function appendNote(existing: string, line: string): string {
  const next = line.trim();
  if (!next) return existing;
  if (!existing) return next;
  if (existing.includes(next)) return existing;
  return `${existing}\n${next}`;
}

/** True when audit progress has enough data to usefully open the create form. */
export function hasAuditCreateBookingData(
  progress: Record<string, unknown> | null | undefined,
): boolean {
  if (!progress) return false;
  const keys = [
    "pickup",
    "dropoff",
    "pickupDate",
    "pickupTime",
    "firstName",
    "surname",
    "email",
    "mobile",
    "selectedVehicleId",
    "flightNo",
    "notes",
  ];
  return keys.some((key) => {
    const value = progress[key];
    if (value == null || value === "") return false;
    if (isMasked(key, value)) return false;
    return true;
  });
}

/**
 * Map website audit customer progress into dashboard BookingEdit field patches.
 */
export function mapAuditProgressToBookingForm(
  progress: Record<string, unknown>,
  bookingSource?: string,
): AuditBookingPrefillFields {
  const out: AuditBookingPrefillFields = {};

  const firstName = asTrimmedString(progress.firstName);
  const surname = asTrimmedString(progress.surname);
  if (firstName && !isMasked("firstName", firstName)) out.pasengerName = firstName;
  if (surname && !isMasked("surname", surname)) out.surName = surname;

  const email = asTrimmedString(progress.email);
  if (email && !isMasked("email", email)) out.email = email;

  const cellno = buildCellno(progress);
  if (cellno) out.cellno = cellno;

  const pickup = asTrimmedString(progress.pickup);
  if (pickup) {
    out.manualPickupAddress = pickup;
    out.startpoint = pickup;
    const postal = extractUkPostcode(pickup);
    if (postal) out.pickupPostalCode = postal;
  }

  const dropoff = asTrimmedString(progress.dropoff);
  if (dropoff) {
    out.manualDropOffAddress = dropoff;
    out.endposition = dropoff;
    const postal = extractUkPostcode(dropoff);
    if (postal) out.dropoffPostalCode = postal;
  }

  const pickupLat = asFiniteNumber(
    progress.pickupLatitude ?? progress.pickupLat,
  );
  const pickupLng = asFiniteNumber(
    progress.pickupLongitude ?? progress.pickupLng,
  );
  if (pickupLat != null) out.pickupLatitude = String(pickupLat);
  if (pickupLng != null) out.pickupLongitude = String(pickupLng);

  const dropoffLat = asFiniteNumber(
    progress.dropoffLatitude ?? progress.dropoffLat,
  );
  const dropoffLng = asFiniteNumber(
    progress.dropoffLongitude ?? progress.dropoffLng,
  );
  if (dropoffLat != null) out.dropoffLatitude = String(dropoffLat);
  if (dropoffLng != null) out.dropoffLongitude = String(dropoffLng);

  const pickupDate = asTrimmedString(progress.pickupDate);
  const pickupTime = asTrimmedString(progress.pickupTime);
  const datetimeLocal = buildPickupDatetimeLocal(pickupDate, pickupTime);
  if (datetimeLocal) {
    out.pickupDateTime = datetimeLocal;
    const iso = ukDatetimeLocalToIso(datetimeLocal);
    if (iso) Object.assign(out, syncPickupDatetimeMirrorFields(iso));
  }

  const adults = asFiniteNumber(progress.adults);
  const children = asFiniteNumber(progress.children);
  if (adults != null && adults >= 0) out.noOfAdult = Math.floor(adults);
  if (children != null && children >= 0) out.noOfChild = Math.floor(children);
  if (out.noOfAdult != null || out.noOfChild != null) {
    out.noofPassenger = (out.noOfAdult ?? 1) + (out.noOfChild ?? 0);
  }

  const luggage = asFiniteNumber(progress.luggage);
  if (luggage != null && luggage >= 0) {
    out.luggagekg = luggage;
    out.luggageDesc = String(luggage);
  }

  const childSeat = asBoolFlag(progress.childSeat);
  if (childSeat != null) out.childSeat = childSeat;

  const wheelchair = asBoolFlag(progress.wheelchair);
  if (wheelchair != null) out.disableSeat = wheelchair;

  const journeyType = asTrimmedString(progress.journeyType);
  if (journeyType === "return" || journeyType === "wait_return") {
    out.returnTrip = 1;
  }

  const airportPickupType = asTrimmedString(progress.airportPickupType);
  if (airportPickupType === "meet_greet") out.meetGreet = 1;
  if (airportPickupType === "curbside") out.meetGreet = 0;

  const flightNo = asTrimmedString(progress.flightNo);
  if (flightNo) out.flightNo = flightNo;

  const arrivingFrom = asTrimmedString(progress.arrivingFrom);
  if (arrivingFrom) out.arrivalFrom = arrivingFrom;

  const terminal = asTrimmedString(progress.terminal);
  if (terminal) out.terminal = terminal;

  const vehicleId = asTrimmedString(progress.selectedVehicleId);
  if (vehicleId) out.vehicleTypeId = vehicleId;

  const vehicleName = asTrimmedString(progress.selectedVehicleName);
  if (vehicleName) out.vehicleTypeName = vehicleName;

  const paymentMode = mapPaymentMode(progress.paymentMethod);
  if (paymentMode) out.paymentMode = paymentMode;

  const fare = asFiniteNumber(progress.fare);
  if (fare != null && fare > 0) {
    out.amount = fare;
    out.calculatedfare = fare;
    out.estimateFareNet = fare;
  }

  const returnDate = asTrimmedString(progress.returnDate);
  if (returnDate) out.returnDate = returnDate;
  const returnTime = normalizeTimeHHmm(asTrimmedString(progress.returnTime));
  if (returnTime) {
    out.returnTime = returnTime;
    out.returnTimeStr = returnTime;
  }

  let notes = asTrimmedString(progress.notes);
  if (asBoolFlag(progress.assistanceDog) === 1) {
    notes = appendNote(notes, "Assistance dog");
  }
  const coupon = asTrimmedString(progress.couponCode);
  if (coupon) notes = appendNote(notes, `Coupon: ${coupon}`);
  if (notes) out.additionalInfo = notes;

  if (bookingSource) out.bookingSource = bookingSource;

  return out;
}

export function buildAuditCreateBookingState(
  progress: Record<string, unknown>,
  bookingSource?: string,
): AuditCreateBookingState {
  return {
    auditPrefill: progress,
    bookingSource,
  };
}
